import React, { useEffect } from 'react';
import { SEOData } from '../../utils/seo';

interface SEOHeadProps {
  seoData: SEOData;
}

const SEOHead: React.FC<SEOHeadProps> = ({ seoData }) => {
  useEffect(() => {
    // Basic metadata
    document.title = seoData.title;
    document.documentElement.lang = 'en';

    // Remove existing and add meta tags
    const setMetaTag = (name: string, content: string, isProperty = false) => {
      const selector = isProperty ? `meta[property="${name}"]` : `meta[name="${name}"]`;
      let meta = document.querySelector(selector) as HTMLMetaElement;
      
      if (meta) {
        meta.content = content;
      } else {
        meta = document.createElement('meta');
        if (isProperty) {
          meta.setAttribute('property', name);
        } else {
          meta.setAttribute('name', name);
        }
        meta.content = content;
        document.head.appendChild(meta);
      }
    };

    // Basic metadata
    setMetaTag('description', seoData.description);
    setMetaTag('keywords', seoData.keywords);
    
    // Open Graph
    setMetaTag('og:title', seoData.openGraph.title, true);
    setMetaTag('og:description', seoData.openGraph.description, true);
    setMetaTag('og:image', seoData.openGraph.image, true);
    setMetaTag('og:url', seoData.openGraph.url, true);
    setMetaTag('og:type', seoData.openGraph.type, true);
    setMetaTag('og:locale', 'en_US', true);
    setMetaTag('og:site_name', 'LiberaCure', true);
    
    // Twitter Card
    setMetaTag('twitter:card', seoData.twitter.card);
    setMetaTag('twitter:title', seoData.twitter.title);
    setMetaTag('twitter:description', seoData.twitter.description);
    setMetaTag('twitter:image', seoData.twitter.image);
    
    // Additional SEO meta tags
    setMetaTag('robots', 'index,follow');
    setMetaTag('author', 'LiberaCure');
    setMetaTag('viewport', 'width=device-width, initial-scale=1.0');

    // Medical trust enhancement
    setMetaTag('medical-disclaimer', 'Information on this site does not replace professional medical advice. Please consult a doctor before using any medication.');
    setMetaTag('legal-notice', 'International purchasing assistance is only available within personal use limits.');
    
    // Canonical URL
    let canonicalLink = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (canonicalLink) {
      canonicalLink.href = seoData.openGraph.url;
    } else {
      canonicalLink = document.createElement('link');
      canonicalLink.rel = 'canonical';
      canonicalLink.href = seoData.openGraph.url;
      document.head.appendChild(canonicalLink);
    }
    
    // JSON-LD structured data
    const existingSchemas = document.querySelectorAll('script[type="application/ld+json"]');
    existingSchemas.forEach(script => script.remove());
    
    if (seoData.jsonLD) {
      seoData.jsonLD.forEach((schema, index) => {
        const script = document.createElement('script');
        script.type = 'application/ld+json';
        script.textContent = JSON.stringify(schema);
        script.id = `json-ld-${index}`;
        document.head.appendChild(script);
      });
    }
  }, [seoData]);
  
  return null; // Metadata is applied directly to the document
};

export default SEOHead;